import { useState } from 'react'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { PlacementId } from '../../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import { useEmbroideryAvailability } from '../../../../../../containers/EmbroideryContainer/providers/EmbroideryAvailabilityProvider'
import { useEmbroiderySelection } from '../../../../../../containers/EmbroideryContainer/providers/EmbroiderySelectionProvider'
import { EmbroideryItemOfType } from '../../../../../../containers/EmbroideryContainer/providers/types'
import { EmbroideryIcon } from '../../../../../../containers/EmbroideryContainer/useEmbroideryDesigns'
import { reportSelectionRefused } from '../../../../../../containers/EmbroideryContainer/utils/reportEmbroideryError'
import { PanelBody } from '../../../styles'
import { FormHeader } from '../../FormHeader/FormHeader'
import { ModalFooter } from '../../ModalFooter/ModalFooter'
import { PlacementOccupants } from '../../PlacementOccupants/PlacementOccupants'
import { PlacementPicker } from '../../PlacementPicker/PlacementPicker'
import { useShowDraftInPreview } from '../../preview/PreviewDraftContext'
import { IconPicker } from '../IconPicker/IconPicker'

export type IconPlacementEditorProps = {
  /** Leaves the editor once the selection is saved. */
  onDone(): void
  /** Leaves the editor from the header, abandoning whatever is still a draft. */
  onBack(): void
}

export const IconPlacementEditor: React.FC<IconPlacementEditorProps> = ({ onDone, onBack }) => {
  const { items, saveItemsOfType } = useEmbroiderySelection()
  const { placementsForType } = useEmbroideryAvailability()
  const {
    embroidery: {
      modal: {
        backButton: backLabel,
        forms: { iconOption, submissionButtons, titles, validationErrorMessages },
      },
      placement,
    },
  } = useTranslation()

  const iconItem = items.find(
    (item): item is EmbroideryItemOfType<'icon'> => item.typeKey === 'icon'
  )

  // A new icon starts on the first placement still open to it, in the order the picker lists them.
  const [draftPlacementId, setDraftPlacementId] = useState<PlacementId | undefined>(
    () => iconItem?.placementId ?? placementsForType('icon')[0]?.id
  )
  const [draftIcon, setDraftIcon] = useState<EmbroideryIcon | undefined>(iconItem?.values.icon)
  const [hasPickedIcon, setHasPickedIcon] = useState(false)
  const [placementError, setPlacementError] = useState<string | undefined>()
  const [iconError, setIconError] = useState<string | undefined>()

  useShowDraftInPreview({
    typeKey: 'icon',
    marks:
      draftPlacementId === undefined
        ? []
        : [
            {
              key: iconItem?.instanceId ?? 'draft-icon',
              placementId: draftPlacementId,
              typeKey: 'icon',
              values: { icon: draftIcon },
            },
          ],
    focusedPlacementId: hasPickedIcon && draftIcon ? draftPlacementId : undefined,
  })

  const selectIcon = (icon: EmbroideryIcon) => {
    setDraftIcon(icon)
    setHasPickedIcon(true)
  }

  // An emptied editor saves by taking the icon off the garment, so it needs an icon there to take.
  const isEmpty = draftPlacementId === undefined && draftIcon === undefined
  const isComplete = draftPlacementId !== undefined && draftIcon !== undefined

  const save = () => {
    if (isEmpty && iconItem) {
      const removed = saveItemsOfType('icon', [])
      if (removed.ok) onDone()
      else reportSelectionRefused(removed.reason, 'icon', [])
      return
    }

    const missingPlacement = draftPlacementId === undefined
    const missingIcon = draftIcon === undefined

    setPlacementError(missingPlacement ? validationErrorMessages.requiredIconPlacement : undefined)
    setIconError(missingIcon ? validationErrorMessages.requiredIconSelection : undefined)
    if (missingPlacement || missingIcon) return

    // A refusal means the picker offered a placement the rules forbid, so there is no message for it.
    const saved = saveItemsOfType('icon', [
      {
        instanceId: iconItem?.instanceId,
        placementId: draftPlacementId,
        values: { icon: draftIcon },
      },
    ])
    if (saved.ok) onDone()
    else reportSelectionRefused(saved.reason, 'icon', [draftPlacementId])
  }

  /** Empties the editor, for "Update garment" to take the icon off the garment. */
  const reset = () => {
    setDraftPlacementId(undefined)
    setDraftIcon(undefined)
    setHasPickedIcon(false)
    setPlacementError(undefined)
    setIconError(undefined)
  }

  return (
    <>
      <PanelBody $fillPanel={false}>
        <FormHeader title={titles.icon} onBack={onBack} backLabel={backLabel} />

        <PlacementPicker
          mode='single'
          typeKey='icon'
          label={placement.iconPlacementLabel}
          instructions={iconOption.placementInstructions}
          errorMessage={placementError}
          editingInstanceId={iconItem?.instanceId}
          selectedPlacementId={draftPlacementId}
          onSelectPlacement={setDraftPlacementId}
        />

        <PlacementOccupants typeKey='icon' placementId={draftPlacementId} dividers='around' />

        <IconPicker selectedIcon={draftIcon} onSelectIcon={selectIcon} errorMessage={iconError} />
      </PanelBody>

      <ModalFooter
        primaryLabel={submissionButtons.updateGarmentCta}
        onPrimary={save}
        primaryDisabled={!isComplete && !(isEmpty && iconItem)}
        secondaryLabel={submissionButtons.resetCta}
        onSecondary={reset}
        sticky={false}
      />
    </>
  )
}
